import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useNavigate } from "react-router"
import AppLayout from "../../components/layout/AppLayout"
import { colors, getAccountChartColor } from "../../lib/colors"
import StatCard from "../../components/analytics/StatCard"
import BarChart from "../../components/analytics/BarChart"
import DonutChart from "../../components/analytics/DonutChart"
import PostGrid from "../../components/analytics/PostGrid"
import SummaryTable from "../../components/analytics/SummaryTable"
import PeriodSelectorModal from "../../components/analytics/PeriodSelectorModal"
import { readStoredPeriods, presetToRange } from "../../lib/dateRange"
import type { PeriodEntry } from "../../lib/dateRange"
import { api } from "../../lib/api"
import type { ReelsData, ReelRow, ChartData, PostThumbnail } from "../../types/analytics"
import { useSelectedAccount } from "../../contexts/AccountContext"
import AccountSelectionNotice from "../../components/account/AccountSelectionNotice"
import FreeWindowNotice from "../../components/account/FreeWindowNotice"
import { useEarliestAccountDate } from "../../hooks/useEarliestAccountDate"
import { PAGE_TITLES } from "../../lib/navItems"
import { downloadCsv, downloadChartAsPng, chartDataToCsvRows, todayStr } from "../../lib/csv"
import "../../styles/analytics.scss"

const STORAGE_KEY = "lyto.reelsPeriod"

const SUMMARY_COLUMNS = [
  { key: "posts",          label: "投稿数" },
  { key: "frequency",      label: "投稿頻度" },
  { key: "plays",          label: "再生数" },
  { key: "likes",          label: "いいね" },
  { key: "comments",       label: "コメント" },
  { key: "shares",         label: "シェア" },
  { key: "saves",          label: "保存" },
  { key: "reach",          label: "リーチ" },
  { key: "engagement_rate", label: "ENG率" },
  { key: "avg_watch_time", label: "平均視聴時間(ms)" },
  { key: "full_play_rate", label: "完全視聴率" },
]

const fmtDateRange = (p: PeriodEntry) =>
  `${p.startDate.replace(/-/g, "/")} 〜 ${p.endDate.replace(/-/g, "/")}`

const ReelsPage = () => {
  const { selectedAccountIds, organizations, setSingleAccountOnly } = useSelectedAccount()
  const earliestDate = useEarliestAccountDate()
  const navigate = useNavigate()
  const playsChartRef           = useRef<HTMLDivElement>(null)
  const engagementBreakdownRef  = useRef<HTMLDivElement>(null)

  const freeWindowAccount = selectedAccountIds
    .map(id => organizations.flatMap(o => o.accounts).find(a => a.id === id))
    .find(a => a?.plan === "free")

  const accountColorMap = useMemo(() => {
    const map: Record<string, string> = {}
    selectedAccountIds.forEach((id, i) => {
      const account = organizations.flatMap(o => o.accounts).find(a => a.id === id)
      if (account) map[account.username] = getAccountChartColor(i).border
    })
    return map
  }, [selectedAccountIds, organizations])

  const applyAccountColors = (chartData: ChartData): ChartData => ({
    ...chartData,
    datasets: chartData.datasets.map((ds, i) => {
      const c = getAccountChartColor(i)
      return { ...ds, backgroundColor: ds.backgroundColor ?? c.bg, borderColor: ds.borderColor ?? c.border }
    }),
  })

  const applyPostColors = (posts: PostThumbnail[] | null | undefined) =>
    posts?.map(p => ({ ...p, color: p.username ? accountColorMap[p.username] : undefined })) ?? null

  const [dataByPeriod, setDataByPeriod] = useState<(ReelsData | null)[]>([null])
  const [loading, setLoading]           = useState(true)
  const [periods, setPeriods]           = useState<PeriodEntry[]>(() => readStoredPeriods(STORAGE_KEY))
  const [refreshingId, setRefreshingId] = useState<number | null>(null)

  useEffect(() => {
    if (!earliestDate) return
    setPeriods(prev => {
      if (prev[0]?.preset !== "all") return prev
      return [{ ...prev[0], ...presetToRange("all", earliestDate) }, ...prev.slice(1)]
    })
  }, [earliestDate])

  useEffect(() => {
    setSingleAccountOnly(periods.length > 1)
  }, [periods, setSingleAccountOnly])

  const fetchData = useCallback(async () => {
    if (selectedAccountIds.length === 0) { setLoading(false); return }
    setLoading(true)
    try {
      const results = await Promise.all(
        periods.map(p => {
          const params = new URLSearchParams({ start_date: p.startDate, end_date: p.endDate })
          selectedAccountIds.forEach(id => params.append("instagram_account_ids[]", String(id)))
          return api.get<ReelsData>(`/analytics/reels?${params}`).catch(() => null)
        })
      )
      setDataByPeriod(results)
    } catch {
    } finally {
      setLoading(false)
    }
  }, [selectedAccountIds, periods])

  useEffect(() => { fetchData() }, [fetchData])

  const handleRefresh = async (postId: number) => {
    setRefreshingId(postId)
    try {
      await api.post(`/analytics/posts/${postId}/refresh`, {})
    } finally {
      setRefreshingId(null)
    }
  }

  const combinedPlaysChart = useMemo(() => {
    const p0Data = dataByPeriod[0]
    if (!p0Data) return { labels: [] as string[], datasets: [] }
    if (dataByPeriod.length <= 1) return applyAccountColors(p0Data.plays_trend)
    const p0Chart = applyAccountColors(p0Data.plays_trend)
    return {
      labels: p0Chart.labels,
      datasets: dataByPeriod.flatMap((data, pi) => {
        if (!data || pi >= periods.length) return []
        const chart = applyAccountColors(data.plays_trend)
        const prefix = `${fmtDateRange(periods[pi])} | `
        return chart.datasets.map((ds, di) => {
          const c = getAccountChartColor(pi * p0Chart.datasets.length + di)
          return { ...ds, label: `${prefix}${ds.label}`, borderColor: c.border, backgroundColor: c.bg }
        })
      }),
    }
  }, [dataByPeriod, periods])

  const fmt = (v: number | null) => v != null ? v.toLocaleString() : "—"
  const data0 = dataByPeriod[0]

  return (
    <AppLayout pageTitle={PAGE_TITLES["/reels"]}>
      <div className="analytics">
        {freeWindowAccount && <FreeWindowNotice days={freeWindowAccount.features.free_window_days} />}
        {selectedAccountIds.length === 0 ? (
          <AccountSelectionNotice />
        ) : (
          <>
            <div className="analytics__header">
              <PeriodSelectorModal
                periods={periods}
                onChange={setPeriods}
                storageKey={STORAGE_KEY}
                allTimeStart={earliestDate}
                maxDays={freeWindowAccount?.features.free_window_days}
              />
            </div>

            {loading ? (
              <p style={{ color: colors.gray400 }}>読み込み中…</p>
            ) : !data0 ? (
              <p style={{ color: colors.gray400 }}>データを取得できませんでした</p>
            ) : (
              <>
                {/* StatCards - メイン期間のみ */}
                <div className="analytics__stats-grid">
                  <StatCard label="投稿数" stat={data0.stats.posts} unit="件" />
                  <StatCard label="再生数" stat={data0.stats.plays} />
                  <StatCard label="いいね" stat={data0.stats.likes} />
                  <StatCard label="シェア" stat={data0.stats.shares} />
                </div>

                {/* チャート - 全期間を1チャートに統合 */}
                <div className="analytics__section">
                  <div className="analytics__charts-grid">
                    {combinedPlaysChart.labels.length > 0 && (
                      <div className="chart-card">
                        <div className="chart-card__header">
                          <span className="chart-card__title">再生数推移</span>
                          <div className="chart-card__actions">
                            <button className="summary-table__csv-btn" onClick={() => downloadCsv(chartDataToCsvRows(combinedPlaysChart), `reels_plays_trend_${todayStr()}.csv`)}>↓ CSV</button>
                            <button className="summary-table__csv-btn" onClick={() => downloadChartAsPng(playsChartRef.current, `reels_plays_trend_${todayStr()}.png`)}>↓ PNG</button>
                          </div>
                        </div>
                        <div className="chart-card__canvas-wrap" ref={playsChartRef}>
                          <BarChart data={combinedPlaysChart} dateAxis />
                        </div>
                      </div>
                    )}
                    {data0 && (
                      <div className="chart-card">
                        <div className="chart-card__header">
                          <span className="chart-card__title">エンゲージメント内訳</span>
                          {data0.engagement_breakdown && (
                            <div className="chart-card__actions">
                              <button className="summary-table__csv-btn" onClick={() => downloadCsv(chartDataToCsvRows(data0.engagement_breakdown!), `reels_engagement_${todayStr()}.csv`)}>↓ CSV</button>
                              <button className="summary-table__csv-btn" onClick={() => downloadChartAsPng(engagementBreakdownRef.current, `reels_engagement_${todayStr()}.png`)}>↓ PNG</button>
                            </div>
                          )}
                        </div>
                        <div className="chart-card__canvas-wrap" ref={engagementBreakdownRef}>
                          {data0.engagement_breakdown
                            ? <DonutChart data={data0.engagement_breakdown} />
                            : <p style={{ color: colors.gray400, fontSize: 13 }}>データ収集中です</p>
                          }
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* サマリーテーブル - メイン期間のみ */}
                <div className="analytics__section">
                  <SummaryTable
                    rows={data0.summary}
                    columns={SUMMARY_COLUMNS}
                    title="サマリー"
                    filePrefix="reels_summary"
                  />
                </div>

                {/* ベストリール - メイン期間のみ */}
                <div className="analytics__section">
                  <div className="top-posts">
                    <div className="top-posts__header">
                      <span className="top-posts__title">ベストリール TOP5（ENG率）</span>
                    </div>
                    {!data0.best_reels || data0.best_reels.length === 0
                      ? <div className="top-posts__empty">データ収集中です</div>
                      : <PostGrid posts={applyPostColors(data0.best_reels)} aspect="vertical" onPostClick={(id, accountId) => navigate(`/reels/${id}`, { state: { accountId } })} />
                    }
                  </div>
                </div>

                {/* 投稿一覧 - メイン期間のみ */}
                <div className="analytics__section">
                  <div className="posts-table">
                    <div className="posts-table__header">
                      <span className="posts-table__title">投稿一覧</span>
                      <button
                        className="summary-table__csv-btn"
                        onClick={() => downloadCsv(
                          [
                            ["キャプション", "ユーザー名", "投稿日時", "再生数", "いいね", "コメント", "シェア", "保存", "リーチ", "ENG率(%)", "平均視聴時間(ms)", "完全視聴率(%)", "リプレイ"],
                            ...data0.posts.map((p: ReelRow) => [
                              p.caption ?? "", p.username ?? "", p.posted_at ?? "",
                              p.plays, p.likes, p.comments, p.shares, p.saves, p.reach,
                              p.engagement_rate, p.avg_watch_time, p.full_play_rate, p.replays,
                            ]),
                          ],
                          `reels_posts_${todayStr()}.csv`
                        )}
                      >
                        ↓ CSV
                      </button>
                    </div>
                    <div className="posts-table__wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>投稿</th>
                            <th>再生数</th>
                            <th>いいね</th>
                            <th>コメント</th>
                            <th>シェア</th>
                            <th>保存</th>
                            <th>リーチ</th>
                            <th>ENG率</th>
                            <th>平均視聴時間</th>
                            <th>完全視聴率</th>
                            <th>リプレイ</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {data0.posts.map((post: ReelRow) => (
                            <tr key={post.id} className="posts-table__row--clickable" onClick={() => navigate(`/reels/${post.id}`, { state: { accountId: post.instagram_account_id } })}>
                              <td>
                                <div className="posts-table__thumb-cell">
                                  {post.thumbnail_url
                                    ? <img src={post.thumbnail_url} alt="" className="posts-table__thumbnail" />
                                    : <div className="posts-table__no-thumb">📷</div>
                                  }
                                  <div className="posts-table__thumb-meta">
                                    {post.username && (
                                      <span
                                        className="posts-table__username"
                                        style={{ background: accountColorMap[post.username] ?? colors.gray400 }}
                                      >
                                        @{post.username}
                                      </span>
                                    )}
                                    <span className="posts-table__caption">{post.caption ?? "—"}</span>
                                  </div>
                                </div>
                              </td>
                              <td className={post.plays == null ? "null-value" : ""}>{fmt(post.plays)}</td>
                              <td className={post.likes == null ? "null-value" : ""}>{fmt(post.likes)}</td>
                              <td className={post.comments == null ? "null-value" : ""}>{fmt(post.comments)}</td>
                              <td className={post.shares == null ? "null-value" : ""}>{fmt(post.shares)}</td>
                              <td className={post.saves == null ? "null-value" : ""}>{fmt(post.saves)}</td>
                              <td className={post.reach == null ? "null-value" : ""}>{fmt(post.reach)}</td>
                              <td className={post.engagement_rate == null ? "null-value" : ""}>
                                {post.engagement_rate != null ? `${post.engagement_rate}%` : "—"}
                              </td>
                              <td className={post.avg_watch_time == null ? "null-value" : ""}>
                                {post.avg_watch_time != null ? (() => { const s = Math.round(post.avg_watch_time! / 1000); return s >= 60 ? `${Math.floor(s / 60)}分${s % 60}秒` : `${s}秒` })() : "—"}
                              </td>
                              <td className={post.full_play_rate == null ? "null-value" : ""}>
                                {post.full_play_rate != null ? `${post.full_play_rate}%` : "—"}
                              </td>
                              <td className={post.replays == null ? "null-value" : ""}>{fmt(post.replays)}</td>
                              <td>
                                {post.can_refresh && (
                                  <button
                                    className="posts-table__refresh-btn"
                                    disabled={refreshingId === post.id}
                                    onClick={e => { e.stopPropagation(); handleRefresh(post.id) }}
                                  >
                                    {refreshingId === post.id ? "…" : "更新"}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}

export default ReelsPage
